// src/pages/merchant-without-customer/NoCustomerContainers.js
import { useState, useEffect, useContext, forwardRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// Utils
import { toMoney } from "../../utils/toMoney";
import useLanguage from "../../utils/useLanguage";
import { DataContext } from "../../utils/DataContext";
import { UserAuth } from "../../utils/AuthContext";
import { formatDate } from "../../utils/toDate";

// Assets


// Components
import CustomCheckbox from "../../components/CustomCheckbox";
import CustomInput from "../../components/CustomInput";
import Dropdown from "../../components/Dropdown";
import CustomButton from "../../components/CustomButton";
import CustomLink from "../../components/CustomLink";
import InformationBox from "../../components/InformationBox";
import UserSection from "../../components/UserSection";
import BackToDashboard from "../../components/BackToDashboard";
import Loader from "../../components/Loader";

// Page sections
import AddFullNoCustomerContainer from "./AddFullNoCustomerContainer";

const NoCustomerContainers = forwardRef((props, ref) => {
  const { t } = useTranslation("general");

  const rows = [
    {
      title: "containerNumber",
      value: "containerNumber",
    },
    {
      title: "supplier",
      value: "supplier",
    },
    {
      title: "material",
      value: "material",
    },
    {
      title: "numberOfBoxes",
      value: "numberOfBoxes",
    },
    {
      title: "measuringUnit",
      value: "measuringUnit",
    },
    {
      title: "finalDestination",
      value: "finalDestination",
    },
    {
      title: "expectedArrivalDate",
      value: "expectedArrivalDate",
    },

    {
      title: "shipper",
      value: "shipper",
    },
    {
      title: "exitDate",
      value: "exitDate",
    },
    {
      title: "dateOfEntry",
      value: "dateOfEntry",
    },
    {
      title: "shippingDate",
      value: "shippingDate",
    },
    {
      title: "permitsReceivingDate",
      value: "permitsReceivingDate",
    },
    {
      title: "company",
      value: "subCompany",
    },
    {
      title: "createdBy",
      value: "createdBy",
    },
    {
      title: "notes",
      value: "notes",
    },
  ];

  const [chartModalOpen, setChartModalOpen] = useState(false);
  const [selectedTitle, setSelectedTitle] = useState({
    title: t("containerNumber"),
    value: "containerNumber",
  });
  const [searchQuery, setSearchQuery] = useState("");

  const { currentDirection } = useLanguage();

  const [showAddContainerModal, setShowAddContainerModal] = useState(false);
  const [showAddInvoiceButton, setShowAddInvoiceButton] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchBySupplier, setSearchBySupplier] = useState(null);

  const { userData } = useContext(UserAuth);

  const {
    selectedSupplier,
    loading,
    setLoading,
    shippedContainers,
    setShippedContainers,
    getSuppliers,
    suppliers,
    currentBalance,
    fetchShippedContainers,
    fetchArchivedShippedContainers,
    selectedDate,
    setSelectedDate,
    setNetworkError,
    searchError,
    searchErrorMessage,
    shippedContainersCurrentPage,
    shippedContainersCount,
    shippedContainersTotalPages,
    setShippedContainersCurrentPage,
    subCompanies,
    selectedSubCompany,
    getSubCompanies,
    handleSelectSubCompany,
    selectedShippedContainersStatus,
    setSelectedShippedContainersStatus,
    shippedContainersBalances: balances,
    setShippedContainersBalances: setBalances,
  } = useContext(DataContext);

  const handleSelectEmployee = (employee) => {
    setSelectedEmployee(employee);
  };

  const handleSearchBySupplier = (supplier) => {
    setSearchBySupplier(supplier);
  };

  const getEmployees = async () => {
    const token =
      localStorage.getItem("SuperAdminToken") ||
      localStorage.getItem("adminToken");

    const organizationId = userData?.organizationId;

    try {
      setLoading(true);

      const response = await axios.get(
        `${process.env.REACT_APP_URL}/getEmployees`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "User-Id": organizationId,
          },
        }
      );
      if (response?.status === 200) {
        setLoading(false);
      }

      if (response.data && response.data.employees) {
        const transformedResults = response.data.employees.map((emp) => {
          return {
            ...emp,
            name: emp?.username ? emp?.username : null,
          };
        });

        setEmployees(transformedResults);
      } else {
        console.error("Failed to get the users");
      }
    } catch (error) {
      setLoading(false);
      setNetworkError(true);
      console.error("Failed to get the users", error);
    }
  };

  // * Reset to default
  useEffect(() => {
    return () => {
      setSelectedShippedContainersStatus({
        title: "Active",
        value: "active",
      });
    };
  }, []);

  useEffect(() => {
    let req;

    if (selectedShippedContainersStatus?.value === "active") {
      req = async () => {
        const response = await fetchShippedContainers(
          null,
          null,
          selectedSubCompany?.id,
          null,
          null,
          true
        );
        setBalances(response?.balance?._sum);
      };
    } else {
      req = async () => {
        const response = await fetchArchivedShippedContainers(
          selectedSupplier?.id,
          null,
          selectedSubCompany?.id,
          null,
          null,
          true
        );
        setBalances(response?.balance?._sum);
      };
    }
    req();
  }, [shippedContainersCurrentPage]);

  const [selectAll, setSelectAll] = useState(false);
  const [searchBy, setSearchBy] = useState("");

  const handleShowChart = () => {
    setChartModalOpen(true);
  };

  const handleSelectAll = () => {
    setSelectAll(!selectAll);

    // Update the selected status for all rows in the data array
    // setData((prevData) => {
    //   return prevData.map((row) => {
    //     return { ...row, selected: !selectAll };
    //   });
    // });
  };

  const handleSearch = async (query = searchQuery) => {
    if (typeof query !== "string") {
      query = searchQuery;
    }
    if (selectedShippedContainersStatus?.title === "Active") {
      await fetchShippedContainers(
        selectedSupplier?.id,
        null,
        selectedSubCompany?.id,
        selectedTitle.value,
        query || selectedDate || selectedEmployee?.id || searchBySupplier?.id,
        true
      );
    } else {
      await fetchArchivedShippedContainers(
        selectedSupplier?.id,
        null,
        selectedSubCompany?.id,
        selectedTitle.value,
        query || selectedDate || selectedEmployee?.id || searchBySupplier?.id,
        true
      );
    }
  };

  const handleShowInputFields = () => {
    setShowAddContainerModal(true);
    // navigate("/createShippedContainerPage");
  };

  // const handleSearchType = () => console.log("test");

  const handleSearchType = async (type) => {
    // console.log("type", type);
    setSelectedTitle(type);
    setSelectedDate("");
    setSearchBySupplier(null);
    // setSelectedEmployee(null);

    setSearchQuery("");
    setSearchBy("");
    if (
      type.value === "expectedArrivalDate" ||
      type.value === "exitDate" ||
      type.value === "dateOfEntry" ||
      type.value === "shippingDate" ||
      type.value === "permitsReceivingDate"
    ) {
      setSearchBy("date");
    }

    if (type.value === "supplier") {
      setSearchBy("supplier");

      if (!suppliers.length) {
        await getSuppliers();
      }
    }

    if (type.value === "subCompany") {
      setSearchBy("subCompany");
      await getSubCompanies();
    }

    if (type.value === "createdBy") {
      setSearchBy("createdBy");
      await getEmployees();
    }

    if (type.value === "numberOfBoxes") {
      setSearchBy("numberOfBoxes");
    }
  };

  const handleChangeStatus = (status) => {
    const { value } = status;
    setSelectedShippedContainersStatus(status);
    setShippedContainersCurrentPage(1);
    setSearchQuery("");

    if (value === "active") {
      setShowAddInvoiceButton(true);
      const req = async () => {
        const response = await fetchShippedContainers(
          null,
          null,
          selectedSubCompany?.id,
          null,
          null,
          true
        );
        setBalances(response?.balance?._sum);
      };
      req();
    } else if (value === "archived") {
      setShowAddInvoiceButton(false);
      fetchArchivedShippedContainers(
        selectedSupplier?.id,
        null,
        selectedSubCompany?.id,
        null,
        null,
        true
      );
    }
  };

  // start print

  const [analyzeLoading, setAnalyzeLoading] = useState(false);

  const [isAnalyzable, setIsAnalyzable] = useState(false);

  // end print

  const balanceStyle =
    currentBalance?.value >= 0 ? "positive-value" : "negative-value";

  const hiddenBalances = selectedSupplier?.id === "All";

  return (
    <>
      <main className="e-container">

        <div className="invoice-table-container">
          <UserSection
            handleShowChart={handleShowChart}
            page="noCustomerContainers"
          />

          <div className="under-user-section">
            <div className="page-title">{t("loadingAndUnloading")}</div>
            <CustomButton
              elementType={Link}
              title={t("invoicesPage")}
              to="/noCustomerInvoices"
              icon="invoices"
            />
          </div>

          {loading ? (
            <Loader />
          ) : (
            <>
              {!selectedSupplier?.id ? (
                <div className="full-w-button-container">
                  <div className="column-flex-box flex-center-center">
                    <InformationBox>
                      {t("noSuppliersAddFromUser")}
                    </InformationBox>
                    <CustomButton
                      title={t("goToSuppliersPage")}
                      minWidth={140}
                      variant="blank"
                      elementType={Link}
                      to={"/viewSuppliersPage"}
                    />
                  </div>
                </div>
              ) : (
                <>
                  <div className="top-action-buttons responsive-box">
                    <div className="button-group">
                      {showAddInvoiceButton ? (
                        <>
                          <CustomButton
                            title={t("addContainer")}
                            onClick={handleShowInputFields}
                            icon="add"
                            iconSize={20}
                          />
                        </>
                      ) : null}
                      {/* {data.length !== 0 && !showInputFields ? (
                          <CustomButton
                            title={t("print")}
                            onClick={handlePrint}
                            variant="gray"
                            icon={printIcon}
                            iconSize={18}
                          />
                        ) : null} */}
                    </div>

                    <section className="col title-col ">
                      <label>{t("invoicesStatus")}: </label>
                      <Dropdown
                        options={[
                          { title: "Active", value: "active" },
                          { title: "Archived", value: "archived" },
                        ]}
                        selected={selectedShippedContainersStatus}
                        setSelected={handleChangeStatus}
                        thick
                      />
                    </section>
                  </div>
                  <div className="c-search-bar u-margin--bottom_500">
                    <section className="c-search-bar-label-input">
                      <label>{t("search")}: </label>

                      {searchBy === "date" ? (
                        <DatePicker
                          selected={selectedDate}
                          onChange={(date) => {
                            setSelectedDate(date.toLocaleDateString("en-CA"));
                          }}
                          dateFormat="yyyy-MM-dd"
                          placeholderText={t("searchByDate")}
                        />
                      ) : searchBy === "subCompany" ? (
                        <>
                          {subCompanies.length ? (
                            <Dropdown
                              options={subCompanies}
                              selected={selectedSubCompany}
                              setSelected={handleSelectSubCompany}
                              thick
                              nameInAr={true}
                            />
                          ) : (
                            <span className="error-text">
                              {t("noSubCompaniesFound")}
                            </span>
                          )}
                        </>
                      ) : searchBy === "supplier" ? (
                        <>
                          {suppliers?.length ? (
                            <Dropdown
                              options={suppliers}
                              selected={searchBySupplier}
                              setSelected={handleSearchBySupplier}
                              thick
                            />
                          ) : (
                            <span className="error-text">
                              {t("noSuppliers")}
                            </span>
                          )}
                        </>
                      ) : searchBy === "createdBy" ? (
                        <>
                          <Dropdown
                            options={employees}
                            selected={selectedEmployee}
                            setSelected={handleSelectEmployee}
                            thick
                          />
                        </>
                      ) : searchBy === "numberOfBoxes" ? (
                        <CustomInput
                          placeholder={t("search")}
                          value={searchQuery}
                          onChange={(e) => {
                            setSearchQuery(e.target.value);
                          }}
                          resetSearch={() => {
                            setSearchQuery("");
                            handleSearch("");
                          }}
                          thick
                          type="moneyV2"
                        />
                      ) : (
                        <CustomInput
                          placeholder={t("search")}
                          value={searchQuery}
                          onChange={(e) => {
                            setSearchQuery(e.target.value);
                          }}
                          resetSearch={() => {
                            setSearchQuery("");
                            handleSearch("");
                          }}
                          thick
                        />
                      )}
                    </section>

                    <section className="c-search-bar-label-input">
                      <label>{t("searchTitle")}: </label>
                      <Dropdown
                        options={rows?.filter(
                          (row) =>
                            row?.title !== "Customer" &&
                            row?.title !== "Supplier" &&
                            row?.title !== "currency" &&
                            row?.title !== "company"
                        )}
                        selected={selectedTitle}
                        setSelected={handleSearchType}
                        thick
                      />
                    </section>
                    <CustomButton
                      title={t("search")}
                      onClick={handleSearch}
                      icon="search"
                      iconSize={15}
                    />
                  </div>
                  {shippedContainers?.length !== 0 ? (
                    <>
                      <section className="table-container">
                        <table className="i-table">
                          <thead className="table-head">
                            <tr>
                              <th className="bold-text">
                                {/* Select or deselect all */}
                                <CustomCheckbox
                                  checked={selectAll}
                                  onChange={handleSelectAll}
                                  color="primary"
                                />
                              </th>

                              {rows.map((row) => {
                                if (
                                  row?.title !== "company" ||
                                  selectedSubCompany?.id === "All"
                                ) {
                                  return (
                                    <th
                                      key={row?.title}
                                      className={`bold-text cell-title ${
                                        row?.value
                                      } ${
                                        row?.value !== "containerNumber"
                                          ? "mobile-hider"
                                          : ""
                                      }`}
                                    >
                                      {t(row?.title)}
                                    </th>
                                  );
                                }
                              })}
                            </tr>
                          </thead>

                          <tbody className="table-body">
                            {/* These are the existing rows */}
                            {shippedContainers?.map(
                              (row, index) =>
                                !row?.canceled && (
                                  <tr key={index}>
                                    <td
                                      className={`cell ${
                                        currentDirection === "ltr"
                                          ? "rounded-first-cell"
                                          : "rounded-last-cell"
                                      }`}
                                    >
                                      <CustomCheckbox
                                        checked={row?.selected || false}
                                        onChange={(e) => {
                                          const updatedRow = [
                                            ...shippedContainers,
                                          ];
                                          updatedRow[index].selected =
                                            e.target.checked;
                                          setShippedContainers(updatedRow);
                                        }}
                                      />
                                    </td>

                                    <td className="cell invoice-number">
                                      {row?.archived ? (
                                        <p className="archived-invoice">
                                          {row?.containerNumber}
                                        </p>
                                      ) : (
                                        <CustomLink
                                          to={`/noCustomerContainer/${row?.id}`}
                                        >
                                          <p className="link">
                                            {row?.containerNumber}
                                          </p>
                                          {row?.updateCounter > 0 &&
                                            !row?.archived && (
                                              <p className="update-note">
                                                {t("containerHasBeenUpdated")}
                                              </p>
                                            )}
                                        </CustomLink>
                                      )}
                                    </td>

                                    <td className="cell mobile-hider">
                                      {row?.supplier?.name || "-"}
                                    </td>

                                    <td className="cell mobile-hider">
                                      {row?.material || "-"}
                                    </td>

                                    <td className="cell mobile-hider">
                                      {row?.numberOfBoxes || "-"}
                                    </td>

                                    <td className="cell mobile-hider">
                                      {row?.measuringUnit || "-"}
                                    </td>

                                    <td className="cell mobile-hider">
                                      {row?.finalDestination || "-"}
                                    </td>

                                    <td className="cell mobile-hider">
                                      {row?.expectedArrivalDate
                                        ? formatDate(row?.expectedArrivalDate)
                                        : "-"}
                                    </td>

                                    <td className="cell mobile-hider">
                                      {row?.shipper || "-"}
                                    </td>

                                    <td className="cell mobile-hider">
                                      {row?.exitDate
                                        ? formatDate(row?.exitDate)
                                        : "-"}
                                    </td>

                                    <td className="cell mobile-hider">
                                      {row?.dateOfEntry
                                        ? formatDate(row?.dateOfEntry)
                                        : "-"}
                                    </td>

                                    <td className="cell mobile-hider">
                                      {row?.shippingDate
                                        ? formatDate(row?.shippingDate)
                                        : "-"}
                                    </td>

                                    <td className="cell mobile-hider">
                                      {row?.permitsReceivingDate
                                        ? formatDate(row?.permitsReceivingDate)
                                        : "-"}
                                    </td>

                                    {selectedSubCompany?.id === "All" && (
                                      <td className="cell mobile-hider ">
                                        {row?.subCompany?.nameInAr || "-"}
                                      </td>
                                    )}

                                    <td className="cell mobile-hider">
                                      {row?.user?.name ||
                                        row?.user?.username ||
                                        "-"}
                                    </td>

                                    <td
                                      className={`cell notes-cell mobile-hider ${
                                        currentDirection === "ltr"
                                          ? "rounded-last-cell"
                                          : "rounded-first-cell"
                                      }`}
                                    >
                                      {row?.notes || "-"}
                                    </td>
                                  </tr>
                                )
                            )}
                          </tbody>
                        </table>
                      </section>

                      <section className="pagination">
                        <div className="pagination-buttons">
                          <CustomButton
                            variant="blank"
                            title={t("previous")}
                            onClick={() =>
                              setShippedContainersCurrentPage(
                                shippedContainersCurrentPage - 1
                              )
                            }
                            disabled={shippedContainersCurrentPage === 1}
                            icon={
                              currentDirection === "ltr"
                                ? "back"
                                : "next"
                            }
                            iconSize={15}
                          />
                          <span className="pagination-location">
                            {t("page")}: {shippedContainersCurrentPage} -{" "}
                            {shippedContainersTotalPages}
                          </span>
                          <CustomButton
                            title={t("next")}
                            variant="blank"
                            onClick={() =>
                              setShippedContainersCurrentPage(
                                shippedContainersCurrentPage + 1
                              )
                            }
                            disabled={
                              shippedContainersCurrentPage ===
                              shippedContainersTotalPages
                            }
                            icon={
                              currentDirection === "ltr"
                                ? "next"
                                : "back"
                            }
                            iconSize={15}
                            reverseIcon
                          />
                        </div>

                        <div className="pagination-buttons">
                          <span className="pagination-location">
                            {t("totalRecords")}: {shippedContainersCount}
                          </span>
                        </div>
                      </section>

                      {!hiddenBalances && selectedSupplier?.id && (
                        <div className="balances margin-inline o-column o-column--vertical-align_end o-column--gap-size_400 u-margin--top_400">
                          {/* <p className="balance">
                            <span>{t("supplierCurrentBalance")}: </span>
                            <span className={balanceStyle}>
                              {!selectedSupplier?.currency ||
                              selectedSupplier?.currency === "N/A" ? (
                                t("noSupplierSelected")
                              ) : (
                                <div className="o-row o-row--vertical-align_center o-row--gap-size_100">
                                  <span>
                                    {toMoney(balances?.amountForSupplier)}
                                  </span>
                                  <span>{selectedSupplier?.currency}</span>
                                </div>
                              )}
                            </span>
                          </p> */}
                        </div>
                      )}
                    </>
                  ) : (
                    <InformationBox>
                      {searchError ? t(searchErrorMessage) : t("noInvoices")}
                    </InformationBox>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </main>

      {/* Render the ChartModal component */}
      {/* <ChartModal
        open={chartModalOpen}
        onClose={handleCloseChart}
        data={data}
      /> */}

      {showAddContainerModal && (
        <AddFullNoCustomerContainer
          setShowAddContainerModal={setShowAddContainerModal}
        />
      )}
    </>
  );
});

export default NoCustomerContainers;
